import { createCheckoutSession, createCustomerPortalSession, fetchSubscriptionSummary } from "@/services/subscriptionApi";
import { getCurrentAuthUser } from '@/services/authSession';
import { toast } from "sonner";

function isUnauthorizedBillingProbe(error: unknown) {
  if (!(error instanceof Error)) {
    return false;
  }

  return /unauthorized|not authenticated/i.test(error.message);
}

/**
 * Check if user has an outstanding billing action or future transition.
 */
export async function hasPendingPayment(): Promise<boolean> {
  try {
    const user = await getCurrentAuthUser();
    if (!user) return false;

    const summary = await fetchSubscriptionSummary();
    return summary.status === 'requires_action' || Boolean(summary.scheduledTierCode);
  } catch (error) {
    if (!isUnauthorizedBillingProbe(error)) {
      console.error("Error checking pending payment:", error);
    }
    return false;
  }
}

/**
 * Legacy no-op retained for compatibility with older call sites.
 */
export async function markPendingPaymentVerification(): Promise<boolean> {
  return false;
}

/**
 * Start a Stripe checkout session for a paid annual tier.
 */
export async function initiateCheckout(targetTier?: string) {
  try {
    const user = await getCurrentAuthUser();
    if (!user) {
      toast.error("Authentication required");
      return;
    }

    if (targetTier !== 'whatsapp_tier' && targetTier !== 'premium_tier') {
      toast.error("Please choose a paid plan before starting checkout.");
      return;
    }

    const isPending = await hasPendingPayment();
    if (isPending) {
      toast.error("You already have a scheduled or attention-required billing state. Open the subscription tab to review it.");
      return;
    }

    const session = await createCheckoutSession({
      tierCode: targetTier,
    });

    if (!session.url) {
      throw new Error('Stripe checkout session did not include a redirect URL');
    }

    window.location.href = session.url;
  } catch (error) {
    console.error("Error initiating checkout:", error);
    toast.error("Failed to start Stripe checkout. Please try again later.");
  }
}

/**
 * Open the Stripe customer portal for Stripe-managed subscriptions.
 */
export async function manageSubscription() {
  try {
    const portal = await createCustomerPortalSession();
    window.location.href = portal.url;
  } catch (error) {
    console.error("Error opening subscription management:", error);
    toast.error("Failed to open subscription management. Please try again later.", {
      duration: 5000
    });
  }
}

/**
 * Free users now have no subscriber row. This legacy helper is kept as a no-op.
 */
export async function createFreeSubscription() {
  return true;
}
